import { useState } from 'react'
import { CalendarRange, Check } from 'lucide-react'
import { useDateRange, type DateRangePreset } from '@/hooks/useDateRange'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { Sheet } from '@/components/ui/Sheet'
import { cn } from '@/lib/cn'
import { formatDateRange } from '@/lib/format'

const PRESET_LABEL: Record<Exclude<DateRangePreset, 'custom'>, string> = {
  '4w': '4w',
  '12w': '12w',
  '6m': '6m',
  '1y': '1y',
}

const PRESET_OPTIONS = (Object.keys(PRESET_LABEL) as Exclude<DateRangePreset, 'custom'>[]).map(
  (value) => ({
    value,
    label: PRESET_LABEL[value],
  }),
)

const PRESET_LONG: Record<Exclude<DateRangePreset, 'custom'>, string> = {
  '4w': 'Last 4 weeks',
  '12w': 'Last 12 weeks',
  '6m': 'Last 6 months',
  '1y': 'Last year',
}

const dateInputClass =
  'h-11 w-full rounded-md border border-line-strong bg-surface px-3 text-body text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

/** Phone version: a small pill with the range that opens a sheet of presets and custom dates. */
function CompactDateRange() {
  const { from, to, preset, setPreset, setCustomRange } = useDateRange()
  const [open, setOpen] = useState(false)

  return (
    <Sheet
      open={open}
      onOpenChange={setOpen}
      title="Date range"
      description={formatDateRange(from, to)}
      trigger={
        <button
          type="button"
          aria-label={`Date range, ${preset === 'custom' ? formatDateRange(from, to) : PRESET_LONG[preset]}`}
          className={cn(
            'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-line bg-surface-2 px-3 text-small font-semibold tabular-nums text-ink',
            'transition-[background-color,transform] duration-150 active:scale-[0.97]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          <CalendarRange className="size-4 text-ink-3" aria-hidden="true" />
          {preset === 'custom' ? 'Custom' : PRESET_LABEL[preset]}
        </button>
      }
    >
      <ul className="flex flex-col gap-1">
        {PRESET_OPTIONS.map((option) => {
          const active = preset === option.value
          return (
            <li key={option.value}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setPreset(option.value)
                  setOpen(false)
                }}
                className={cn(
                  'flex min-h-12 w-full items-center justify-between gap-3 rounded-md px-3 text-left text-body font-medium transition-colors duration-150',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                  active ? 'bg-surface-2 text-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
                )}
              >
                {PRESET_LONG[option.value]}
                {active && <Check className="size-4 text-accent" aria-hidden="true" />}
              </button>
            </li>
          )
        })}
      </ul>
      <div className="mt-3 rounded-lg border border-line bg-surface p-3">
        <p className="mb-2 text-small font-medium text-ink">Custom</p>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-caption text-ink-3">
            From
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => e.target.value && setCustomRange(e.target.value, to)}
              className={dateInputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-caption text-ink-3">
            To
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => e.target.value && setCustomRange(from, e.target.value)}
              className={dateInputClass}
            />
          </label>
        </div>
      </div>
    </Sheet>
  )
}

export interface DateRangeControlProps {
  /** Phone top bar: a pill that opens a sheet instead of the inline segmented control. */
  compact?: boolean
}

/** Global date-range filter, state kept in the URL (`from`/`to` search params). */
export function DateRangeControl({ compact = false }: DateRangeControlProps) {
  if (compact) return <CompactDateRange />
  return <InlineDateRange />
}

function InlineDateRange() {
  const { from, to, preset, setPreset, setCustomRange } = useDateRange()
  const [showCustom, setShowCustom] = useState(false)

  return (
    <div className="flex items-center gap-2">
      <SegmentedControl
        aria-label="Date range"
        value={preset === 'custom' ? ('custom' as DateRangePreset) : preset}
        onChange={(value) => {
          if (value === 'custom') {
            setShowCustom(true)
            return
          }
          setShowCustom(false)
          setPreset(value as Exclude<DateRangePreset, 'custom'>)
        }}
        options={[...PRESET_OPTIONS, { value: 'custom' as const, label: 'Custom' }]}
      />
      {(showCustom || preset === 'custom') && (
        <div className="flex items-center gap-1.5">
          <label className="sr-only" htmlFor="date-range-from">
            From
          </label>
          <input
            id="date-range-from"
            type="date"
            value={from}
            max={to}
            onChange={(e) => setCustomRange(e.target.value, to)}
            className="h-9 rounded-md border border-line-strong bg-surface px-2 text-small text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          />
          <span className="text-ink-3" aria-hidden="true">
            to
          </span>
          <label className="sr-only" htmlFor="date-range-to">
            To
          </label>
          <input
            id="date-range-to"
            type="date"
            value={to}
            min={from}
            onChange={(e) => setCustomRange(from, e.target.value)}
            className="h-9 rounded-md border border-line-strong bg-surface px-2 text-small text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          />
        </div>
      )}
      <span className="hidden text-small text-ink-2 min-[1100px]:inline">
        {formatDateRange(from, to)}
      </span>
    </div>
  )
}
